/**
 * Auswahl der Karten-Overlays (Wetter, Verkehr) für die Live-Karte.
 *
 * Ein kleines Menü oben rechts auf der Karte, in dem sich Ebenen zuschalten
 * lassen. Anders als die Datenquellen der Zeitachse leben diese nicht in der
 * Adresszeile — sie sind eine Ansichtseinstellung, kein teilbarer Filter, und
 * bleiben im `localStorage`.
 */

import { useEffect, useRef, useState } from "react";
import { Activity, Check, CloudRain, Flame, Layers, Plane, PlugZap, Satellite, Ship, Thermometer, Wind, Zap } from "lucide-react";

export interface OverlayDef {
  id: string;
  name: string;
  Symbol: typeof CloudRain;
  hinweis?: string;
}

/** Verfügbare Overlays. Weitere (Blitze) docken hier an. */
export const OVERLAYS: OverlayDef[] = [
  { id: "regen", name: "Regenradar", Symbol: CloudRain, hinweis: "Niederschlag der letzten 2 Stunden, animiert" },
  { id: "flug", name: "Flugverkehr", Symbol: Plane, hinweis: "Flugzeuge im Kartenausschnitt, alle 20 s" },
  { id: "temp", name: "Temperatur", Symbol: Thermometer, hinweis: "Aktuelle Werte (DWD) an den Geräten" },
  { id: "blitze", name: "Blitze", Symbol: Zap, hinweis: "Einschläge der letzten Minuten (über Home Assistant)" },
  { id: "wind", name: "Wind", Symbol: Wind, hinweis: "Richtung und Stärke an den Geräten (Open-Meteo)" },
  { id: "erdbeben", name: "Erdbeben", Symbol: Activity, hinweis: "Weltweit ab Stärke 2,5, letzte 24 Stunden (USGS)" },
  { id: "iss", name: "Raumstation ISS", Symbol: Satellite, hinweis: "Aktuelle Position der ISS" },
  { id: "laden", name: "Ladestationen", Symbol: PlugZap, hinweis: "E-Ladesäulen im Ausschnitt — Klick zeigt Preise" },
  { id: "schiffe", name: "Schiffe", Symbol: Ship, hinweis: "Schiffe im Ausschnitt (AIS), weltweit — Klick zeigt Name/Tempo" },
  { id: "feuer", name: "Waldbrände", Symbol: Flame, hinweis: "Aktive Feuer/Hotspots, letzte 24 h (NASA FIRMS)" },
];

const ABLAGE = "ribbon.overlays";

/** Die gemerkte Auswahl laden. */
export function overlaysLaden(): Set<string> {
  try {
    const roh = localStorage.getItem(ABLAGE);
    return new Set(roh ? (JSON.parse(roh) as string[]) : []);
  } catch {
    return new Set();
  }
}

function overlaysSpeichern(auswahl: Set<string>): void {
  try {
    localStorage.setItem(ABLAGE, JSON.stringify([...auswahl]));
  } catch {
    // localStorage nicht verfügbar — dann eben nur für diese Sitzung.
  }
}

export function OverlayAuswahl({
  gewaehlt,
  beiWechsel,
}: {
  gewaehlt: Set<string>;
  beiWechsel: (neu: Set<string>) => void;
}) {
  const [offen, setOffen] = useState(false);
  const rahmen = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!offen) return;
    const zu = (e: MouseEvent) => {
      if (rahmen.current && !rahmen.current.contains(e.target as Node)) setOffen(false);
    };
    document.addEventListener("mousedown", zu);
    return () => document.removeEventListener("mousedown", zu);
  }, [offen]);

  const umschalten = (id: string) => {
    const neu = new Set(gewaehlt);
    if (neu.has(id)) neu.delete(id);
    else neu.add(id);
    overlaysSpeichern(neu);
    beiWechsel(neu);
  };

  return (
    <div ref={rahmen} className="absolute left-2 top-14 z-10 lg:top-2">
      <button
        onClick={() => setOffen((o) => !o)}
        className={`flex items-center gap-1.5 rounded-lg border border-border bg-surface px-2.5 py-1.5 text-sm shadow hover:bg-page-bg ${
          gewaehlt.size > 0 ? "text-primary" : "text-text"
        }`}
        title="Overlays"
      >
        <Layers size={16} />
        {gewaehlt.size > 0 && <span className="text-xs font-medium">{gewaehlt.size}</span>}
      </button>

      {offen && (
        <div className="absolute left-0 top-full mt-1 w-60 rounded-lg border border-border bg-surface py-1 shadow-lg">
          {OVERLAYS.map((o) => {
            const aktiv = gewaehlt.has(o.id);
            return (
              <button
                key={o.id}
                onClick={() => umschalten(o.id)}
                className="flex w-full items-start gap-2.5 px-3 py-2 text-left text-sm hover:bg-page-bg"
              >
                <span
                  className="mt-0.5 flex size-4 shrink-0 items-center justify-center rounded border"
                  style={{ backgroundColor: aktiv ? "var(--color-primary)" : "transparent", borderColor: "var(--color-primary)" }}
                >
                  {aktiv && <Check size={11} className="text-white" strokeWidth={3} />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5">
                    <o.Symbol size={14} className="shrink-0 text-text-muted" />
                    <span className="truncate">{o.name}</span>
                  </span>
                  {o.hinweis && <span className="mt-0.5 block text-xs text-text-muted">{o.hinweis}</span>}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
